// components/trust/ReliabilityScore.jsx
// Updated: Dec 30, 2025 - Task 3.3: Renamed "Group Participation" to "Loyalty"

import React, { useState } from 'react';
import { Shield, Star, TrendingUp, ChevronDown, Award, Users, Clock, CheckCircle, Crown } from 'lucide-react';

const ReliabilityScore = ({ 
  score = 3.8, 
  size = "medium", 
  showDetails = false,
  breakdown = null,
  showBadge = true,
  onDetailsClick = null
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Size configurations
  const sizes = {
    small: {
      container: "text-sm",
      icon: "w-4 h-4",
      score: "text-lg",
      badge: "px-2 py-0.5 text-xs"
    },
    medium: {
      container: "text-base",
      icon: "w-5 h-5", 
      score: "text-xl",
      badge: "px-3 py-1 text-sm"
    },
    large: {
      container: "text-lg",
      icon: "w-6 h-6",
      score: "text-2xl", 
      badge: "px-4 py-1.5 text-base"
    }
  };

  const currentSize = sizes[size];

  // Get color based on score
  const getColor = (score) => {
    if (score >= 4.5) return { text: "text-green-600", bg: "bg-green-100", border: "border-green-200" };
    if (score >= 4.0) return { text: "text-blue-600", bg: "bg-blue-100", border: "border-blue-200" };
    if (score >= 3.5) return { text: "text-yellow-600", bg: "bg-yellow-100", border: "border-yellow-200" };
    if (score >= 3.0) return { text: "text-orange-600", bg: "bg-orange-100", border: "border-orange-200" };
    return { text: "text-gray-600", bg: "bg-gray-100", border: "border-gray-200" };
  };

  // Get trust level
  const getTrustLevel = (score) => {
    if (score >= 4.5) return "Expert";
    if (score >= 4.0) return "Trusted";
    if (score >= 3.5) return "Reliable";
    if (score >= 3.0) return "Building";
    return "New Member";
  };

  const colors = getColor(score);
  const trustLevel = getTrustLevel(score);

  // Breakdown display component
  const BreakdownItem = ({ label, value, icon: Icon }) => {
    const itemColors = getColor(value);
    return (
      <div className="flex items-center justify-between py-2">
        <div className="flex items-center gap-2">
          <Icon className={`w-4 h-4 ${itemColors.text}`} />
          <span className="text-gray-700">{label}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star
                key={star}
                size={12}
                className={`${star <= value ? itemColors.text : 'text-gray-300'} ${star <= value ? 'fill-current' : ''}`}
              />
            ))}
          </div>
          <span className={`text-sm font-medium ${itemColors.text}`}>{value.toFixed(1)}</span>
        </div>
      </div>
    );
  };

  const handleDetailsClick = () => {
    if (onDetailsClick) {
      onDetailsClick();
    } else {
      setIsExpanded(!isExpanded);
    }
  };

  return (
    <div className={`${currentSize.container}`}>
      {/* Main Score Display */}
      <div className="flex items-center gap-3">
        <div className={`flex items-center gap-2 ${colors.bg} ${colors.border} border rounded-lg px-3 py-2`}>
          <Shield className={`${currentSize.icon} ${colors.text}`} />
          <div className="flex items-center">
            <span className={`font-bold ${currentSize.score} ${colors.text}`}>
              {score.toFixed(1)}
            </span>
            <span className="text-gray-500 ml-1">/5.0</span>
          </div>
        </div>
        
        {showBadge && (
          <div className={`${colors.bg} ${colors.text} ${currentSize.badge} rounded-full font-medium`}>
            {trustLevel}
          </div>
        )}
        
        {showDetails && breakdown && (
          <button 
            onClick={handleDetailsClick}
            className="flex items-center gap-1 text-gray-600 hover:text-gray-800 transition-colors"
          >
            <span className="text-sm">Details</span>
            <ChevronDown 
              size={16} 
              className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
            />
          </button>
        )}
      </div>

      {/* Expanded Breakdown */}
      {isExpanded && breakdown && (
        <div className="mt-4 bg-white border border-gray-200 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-3">Trust Score Breakdown</h4>
          
          <div className="space-y-1">
            {/* UPDATED: "Group Participation" → "Loyalty" with Crown icon */}
            <BreakdownItem 
              label="Loyalty" 
              value={breakdown.loyalty || breakdown.groupParticipation || 0} 
              icon={Crown}
            />
            <BreakdownItem 
              label="Job Completion" 
              value={breakdown.jobCompletion || 0} 
              icon={CheckCircle}
            />
            <BreakdownItem 
              label="Peer Ratings" 
              value={breakdown.peerRatings || 0} 
              icon={Star}
            />
            <BreakdownItem 
              label="Skill Verification" 
              value={breakdown.skillVerification || 0} 
              icon={Award}
            />
            <BreakdownItem 
              label="Response Time" 
              value={breakdown.responseTime || 0} 
              icon={Clock}
            />
          </div>
          
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Overall Trust Score</span>
              <span className={`font-bold ${colors.text}`}>{score.toFixed(1)}</span>
            </div>
          </div>

          {score < 4.0 && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <div className="flex items-start gap-2">
                <TrendingUp className="w-4 h-4 text-blue-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-900">Improve Your Trust Score</p>
                  <p className="text-sm text-blue-700 mt-1">
                    Make purchases, refer friends, and verify your skills to unlock premium opportunities!
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ReliabilityScore;